import React, { useState, useEffect, useContext } from "react";
import { Table, Button, Badge, Card, Pagination } from "react-bootstrap";
import { useNavigate } from "react-router-dom"; 
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api"; 
import UserContext from "../../context/UserContext";

const LeaveApproval = () => {
  const { user } = useContext(UserContext); 
  const navigate = useNavigate(); 
  const [pendingApprovals, setPendingApprovals] = useState([]); 

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Mapping Leave Types to English Labels and Icons
  const leaveTypeMap = {
    0: { label: "Annual Leave", icon: "🌴" },
    1: { label: "Sick Leave", icon: "🩺" },
    2: { label: "Unpaid Leave", icon: "💸" },
    3: { label: "Maternity Leave", icon: "👶" },
    4: { label: "Wedding Leave", icon: "💍" },
    5: { label: "Annual Leave (>= 3 days)", icon: "📅" }
  };

  const getUserId = () => {
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.id || user?.userId || storedUser?.id || storedUser?.userId;
  };

  useEffect(() => {
    fetchPendingApprovals();
  }, [user]);

  const fetchPendingApprovals = async () => {
    try {
      const currentId = getUserId();
      if (!currentId) return;

      const res = await axiosClient.get(`${API_ENDPOINTS.leaveRequests}/Pending`, {
        params: { approverId: currentId }
      }); 
      
      setPendingApprovals(res.data);
      setCurrentPage(1);
    } catch (err) { 
      console.error("Error loading pending approvals:", err); 
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = pendingApprovals.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(pendingApprovals.length / itemsPerPage);

  const getStatusBadge = (status) => {
    switch (status) {
      case -1: return <Badge bg="danger" className="px-3 py-2">MANAGER REJECTED</Badge>;
      case 0: return <Badge bg="warning" text="dark" className="px-3 py-2">PENDING MANAGER</Badge>;
      case 1: return <Badge bg="success" className="px-3 py-2">MANAGER APPROVED</Badge>;
      case 2: return <Badge bg="danger" className="px-3 py-2">HR REJECTED</Badge>;
      case 3: return <Badge bg="info" className="px-3 py-2 text-white">PENDING HR</Badge>;
      case 4: return <Badge bg="success" className="px-3 py-2">HR APPROVED</Badge>;
      case 5: return <Badge bg="danger" className="px-3 py-2">BLOCK MANAGER REJECTED</Badge>;
      case 6: return <Badge bg="primary" className="px-3 py-2">PENDING BLOCK MANAGER</Badge>;
      case 7: return <Badge bg="success" className="px-3 py-2">BLOCK MANAGER APPROVED</Badge>;
      case 8: return <Badge bg="danger" className="px-3 py-2">BOARD REJECTED</Badge>;
      case 9: return <Badge bg="secondary" className="px-3 py-2">PENDING BOARD</Badge>;
      case 10: return <Badge bg="success" className="px-3 py-2">BOARD APPROVED</Badge>;
      case 11: return <Badge bg="success" className="px-3 py-2">FULLY APPROVED</Badge>;
      default: return <Badge bg="secondary" className="px-3 py-2">UNKNOWN</Badge>;
    }
  };

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold text-dark text-uppercase mb-0">Leave Approval Portal</h3>
          <p className="text-muted small mb-0">Review and manage employee leave requests</p>
        </div>
      </div>

      <Card className="shadow-sm border-0 rounded-3 overflow-hidden">
        <Card.Header className="bg-white py-3 border-bottom border-light">
            <h5 className="mb-0 fw-bold text-primary d-flex align-items-center">
              <i className="fas fa-tasks me-2"></i>Requests Awaiting Your Action
            </h5>
        </Card.Header>
        <Card.Body className="p-0">
          <Table hover responsive className="align-middle mb-0">
            <thead style={{ backgroundColor: "#f1f4f9" }}>
              <tr>
                <th className="ps-4 py-3 text-muted fw-bold small text-uppercase">Employee</th>
                <th className="py-3 text-muted fw-bold small text-uppercase">Leave Type</th>
                <th className="py-3 text-muted fw-bold small text-uppercase">Duration</th>
                <th className="py-3 text-muted fw-bold small text-center text-uppercase">Days</th>
                <th className="py-3 text-muted fw-bold small text-uppercase">Status</th>
                <th className="py-3 text-muted fw-bold small text-end pe-4 text-uppercase">Review</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((r) => (
                <tr key={r.id} className="border-bottom border-light">
                  <td className="ps-4 py-3">
                    <div className="fw-bold text-dark">
                      {r.user?.fullName || r.user?.userName || `Staff ID: ${r.userId}`}
                    </div>
                    <div className="text-muted small">Employee ID: #{r.userId}</div>
                  </td>
                  <td>
                    <div className="fw-bold text-secondary">
                      {leaveTypeMap[r.type]?.icon} {leaveTypeMap[r.type]?.label || "Other"}
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center small fw-bold text-dark">
                      {new Date(r.startDate).toLocaleDateString("en-GB")} 
                      <i className="fas fa-long-arrow-alt-right mx-2 text-muted"></i>
                      {new Date(r.endDate).toLocaleDateString("en-GB")}
                    </div>
                  </td>
                  <td className="text-center">
                    <span className="badge rounded-pill bg-light text-dark border px-3">
                      {r.totalDays} Days
                    </span>
                  </td>
                  <td>{getStatusBadge(r.status)}</td>
                  <td className="text-end pe-4">
                    <Button 
                      variant="white" 
                      className="rounded-circle shadow-sm border-1 d-inline-flex align-items-center justify-content-center"
                      style={{ width: "38px", height: "38px", color: "#6366f1", border: "1px solid #eee" }}
                      onClick={() => navigate(`/approve-leave/${r.id}`)} 
                      title="Review Detail"
                    >
                      <i className="fas fa-eye"></i>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {pendingApprovals.length === 0 && (
            <div className="text-center py-5 bg-white">
               <i className="fas fa-check-double text-muted mb-3" style={{ fontSize: '2.5rem', opacity: 0.5 }}></i>
               <h6 className="text-muted fw-normal">All caught up! No requests to approve.</h6>
            </div>
          )}

          {pendingApprovals.length > itemsPerPage && (
            <div className="d-flex justify-content-between align-items-center px-4 py-3 bg-light border-top">
              <span className="text-muted small">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, pendingApprovals.length)} of {pendingApprovals.length}
              </span>
              <Pagination className="mb-0">
                <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} />
                {[...Array(totalPages)].map((_, i) => (
                  <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
                    {i + 1}
                  </Pagination.Item>
                ))}
                <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} />
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default LeaveApproval;